/**
 * ТЗ 0106, этап 3 — доска договоров (`/contracts?view=board`): колонки по
 * статусам Documenso, без перетаскивания (статус ведёт Documenso), и
 * плитки-итоги над списком. Модуль без React, Apollo и i18n.
 */

import moment from 'moment';

import { warsawDaysAgoIso } from 'core/utils/zoomTime';

import { esignStatusTone, EsignTagTone } from './esignModel';

/** Вид раздела в адресе: `?view=board`; список — без параметра. */
export const CONTRACTS_VIEW_PARAM = 'view';
export const CONTRACTS_VIEWS = ['list', 'board'] as const;
export type ContractsView = typeof CONTRACTS_VIEWS[number];

export const parseContractsView = (search: string): ContractsView =>
  new URLSearchParams(search).get(CONTRACTS_VIEW_PARAM) === 'board'
    ? 'board'
    : 'list';

export const withContractsView = (
  search: string,
  view: ContractsView,
): string => {
  const params = new URLSearchParams(search);
  if (view === 'board') params.set(CONTRACTS_VIEW_PARAM, 'board');
  else params.delete(CONTRACTS_VIEW_PARAM);
  return params.toString();
};

/**
 * Колонки доски: черновик, «ошибка отправки» (красная — договор не ушёл и
 * ждёт человека), отправлен (и в пути), открыт, подписан, «не случился»
 * (отклонён, истёк, отменён). Каждый статус — ровно в одной колонке.
 */
export const CONTRACT_BOARD_COLUMNS = [
  { key: 'draft', statuses: ['draft'] },
  { key: 'failed', statuses: ['send_failed'] },
  { key: 'sent', statuses: ['sending', 'sent'] },
  { key: 'opened', statuses: ['opened'] },
  { key: 'completed', statuses: ['completed'] },
  { key: 'closed', statuses: ['rejected', 'expired', 'cancelled'] },
] as const;

export type ContractBoardColumnKey = typeof CONTRACT_BOARD_COLUMNS[number]['key'];

export interface IContractBoardColumn {
  key: ContractBoardColumnKey;
  /** Статусы колонки с учётом выбранных в фильтре. */
  statuses: string[];
}

/**
 * Колонки под фильтром статусов: выбраны статусы — колонка берёт только их,
 * колонка без выбранных — не показывается (как «выбор этапа = только он»).
 * «Ошибка отправки» — только когда такие договоры есть (по счётчикам
 * статусов) или статус выбран явно: пустая красная колонка — лишний шум.
 */
export const contractBoardColumns = (
  selected: ReadonlyArray<string>,
  counts?: Readonly<Record<string, number>>,
): IContractBoardColumn[] =>
  CONTRACT_BOARD_COLUMNS.map((column) => ({
    key: column.key,
    statuses: (column.statuses as readonly string[]).filter(
      (status) => !selected.length || selected.includes(status),
    ),
  })).filter((column) => {
    if (!column.statuses.length) return false;
    if (column.key !== 'failed' || selected.length) return true;
    const known = counts && Object.keys(counts).length > 0;
    return !known || (counts?.send_failed ?? 0) > 0;
  });

/** Число договоров колонки по счётчикам статусов. */
export const contractColumnCount = (
  counts: Readonly<Record<string, number>>,
  statuses: ReadonlyArray<string>,
): number => statuses.reduce((acc, status) => acc + (counts[status] ?? 0), 0);

/** Тон колонки — тон её первого статуса (как у плашки статуса). */
export const contractColumnTone = (
  column: Pick<IContractBoardColumn, 'key'>,
): EsignTagTone => {
  if (column.key === 'closed' || column.key === 'failed') return 'danger';
  if (column.key === 'draft') return 'neutral';
  const base = CONTRACT_BOARD_COLUMNS.find((item) => item.key === column.key);
  return esignStatusTone(base ? base.statuses[base.statuses.length - 1] : '');
};

interface IContractTimes {
  status: string;
  createdAt?: string | null;
  sentAt?: string | null;
}

/**
 * Сколько дней договор ждёт: отправленный и открытый — с отправки, черновик —
 * с создания; подписанный и закрытый не ждут (`null`).
 */
export const contractWaitingDays = (
  row: IContractTimes,
  now: moment.Moment = moment(),
): number | null => {
  const waiting = ['draft', 'send_failed', 'sending', 'sent', 'opened'];
  if (!waiting.includes(row.status)) return null;
  const from = ['sent', 'opened'].includes(row.status)
    ? row.sentAt || row.createdAt
    : row.createdAt;
  if (!from || !moment(from).isValid()) return null;
  return Math.max(0, now.diff(moment(from), 'days'));
};

/** Плитки-итоги над «Договорами» — по счётчикам статусов под фильтрами. */
export interface IContractsSummary {
  awaiting: number;
  signed: number;
  drafts: number;
  lost: number;
}

export const contractsSummary = (
  counts: Readonly<Record<string, number>>,
): IContractsSummary => ({
  awaiting: contractColumnCount(counts, ['sending', 'sent', 'opened']),
  signed: contractColumnCount(counts, ['completed']),
  drafts: contractColumnCount(counts, ['draft', 'send_failed']),
  lost: contractColumnCount(counts, ['rejected', 'expired', 'cancelled']),
});

/* ── ТЗ 0106: подписанные за 30 дней ─────────────────────────────────── */

/** Окно плиток «Подписано за 30 дней» и «Сумма подписанных за 30 дней». */
export const CONTRACTS_SIGNED_RECENT_DAYS = 30;

/**
 * Переменные `ContractSignedRecently`: прочие фильтры списка без статуса
 * (статус — всегда `completed`, его ставит операция) и `signedFrom` —
 * «сегодня по Варшаве минус 30 дней». `today` — для тестов (`YYYY-MM-DD`).
 */
export const toContractsSignedRecentVariables = <
  T extends { status?: string | null }
>(
  filters: T,
  today?: string,
): Omit<T, 'status'> & { signedFrom: string } => {
  const rest: Partial<T> = { ...filters };
  delete rest.status;
  return {
    ...(rest as Omit<T, 'status'>),
    // «За 30 дней» включая сегодня: сегодня и 29 предыдущих дней.
    signedFrom: warsawDaysAgoIso(CONTRACTS_SIGNED_RECENT_DAYS - 1, today),
  };
};

export interface IContractAmount {
  currency: string;
  amount: unknown;
  count: number;
}

/**
 * Суммы по валютам для плитки: без нулевых, PLN первой, дальше по коду
 * валюты. Валюты не складываются — каждая своей строкой.
 */
export const contractAmountRows = <T extends IContractAmount>(
  rows: ReadonlyArray<T>,
): T[] =>
  rows
    .filter(
      (row) => Number(row.amount) !== 0 && Number.isFinite(Number(row.amount)),
    )
    .sort((a, b) => {
      if (a.currency === b.currency) return 0;
      if (a.currency === 'PLN') return -1;
      if (b.currency === 'PLN') return 1;
      return a.currency < b.currency ? -1 : 1;
    });
